<template>
  <div>
    <app-dialog
      v-model="open"
      title="debtors.editPayment.editDataFor"
      primary-action-text="navigation.save"
      secondary-action-text="navigation.cancel"
      :loading="loading"
      :disabled="disableSubmit"
      @submit="onSubmit"
    >
      <ValidationObserver ref="form" tag="form">
        <v-container class="pa-0">
          <app-alert v-if="error" class="mb-4">
            {{ $t('debtors.editDebtorError') }}
          </app-alert>

          <v-row no-gutters>
            <v-col>
              <div class="semi-bold">{{ debtor.name }}</div>
              <span v-if="debtor.address.streetAndHouse">
                {{ debtor.address.streetAndHouse }},&#32;
              </span>
              <span>{{ debtor.address.zip }} {{ debtor.address.city }}</span>
            </v-col>
          </v-row>

          <app-input-row dense label="debtors.paymentMethod" class="mt-4 mb-4">
            <app-select
              v-if="deleteSepa"
              :value="paymentMethodBankTransfer"
              :items="paymentMethodItems"
              required
              disabled
            />

            <app-select
              v-else
              v-model="formData.paymentMethod"
              :items="paymentMethodItems"
              required
            />
          </app-input-row>

          <template v-if="requireSepaData">
            <div
              class="
                caption
                text-uppercase
                grey--text
                semi-bold
                mt-8
                d-flex
                justify-space-between
                align-baseline
              "
            >
              {{ $t('debtors.editPayment.sepaData') }}

              <app-text-btn
                v-if="debtorHasSepaMandate"
                :disabled="disableSepaDeleteBtn"
                :title="$t('debtors.editPayment.deleteSepa')"
                color="primary"
                @click="deleteSepa = !deleteSepa"
              >
                <template #prepend-icon>
                  <v-icon small class="delete-sepa__icon">
                    {{ deleteSepa ? 'mdi-checkbox-marked-outline' : 'mdi-checkbox-blank-outline' }}
                  </v-icon>
                </template>
              </app-text-btn>
            </div>
            <div v-if="deleteSepa" class="my-4">
              <app-alert v-if="debtor.hasActivePermanentDebts">
                {{ $t('debtors.editPayment.debtorHasActivePermanentDebts') }}
                <app-link-btn
                  class="d-block mt-2"
                  title="navigation.toPermanentDebts"
                  variant="muted"
                  :to="activePermanentDebtsRoute"
                />
              </app-alert>

              <app-alert
                v-else
                text="debtors.editPayment.deleteSepaInfo"
                color="primary"
                icon="$iconInfo"
              />
            </div>

            <template v-else>
              <app-input-row dense label="accountOwner">
                <app-text-field
                  v-model="formData.sepa.accountOwner"
                  required
                  name="accountOwner"
                  placeholder="companyOrName"
                />
              </app-input-row>

              <app-input-row dense label="iban">
                <app-text-field
                  v-model="formData.sepa.iban"
                  placeholder="DEXX XX XXXXXXXX"
                  name="iban"
                  rules="required|iban"
                />
              </app-input-row>

              <app-input-row dense class="mt-6">
                <app-checkbox
                  v-model="confirmedSepa"
                  label="permanentDebts.confirmSepa"
                  hide-details
                  required
                />
                <a
                  class="subtitle-1 text-left d-block ml-8"
                  :href="product.documents.sepa.template.path"
                  target="_blank"
                >
                  ({{ product.documents.sepa.template.title }})
                </a>
              </app-input-row>
            </template>
          </template>
        </v-container>
      </ValidationObserver>
    </app-dialog>

    <app-dialog
      v-model="openConfirmation"
      secondary-action-text="navigation.backToDebtor"
      title="debtors.editPayment.confirmed"
      @close="hideAfterSuccess"
    />
  </div>
</template>

<script>
import ShowDialogMixin from '@/mixins/ShowDialogMixin';
import filterChangedProperties from '@/helper/filterChangedProperties';
import { NAMESPACE } from '@/modules/Debtors/store';
import { PAYMENT_METHODS, PaymentMethod } from '@/modules/Debtors/statics/paymentMethod';
import PermanentDebtStatus from '@/modules/PermanentDebts/statics/permanentDebtStatus';
import { mapActions } from 'vuex';

export default {
  name: 'EditPaymentData',

  mixins: [ShowDialogMixin],

  props: {
    debtor: {
      type: Object,
      required: true
    }
  },

  data() {
    return {
      formData: this.createFormData(),
      oldPaymentData: this.createFormData(),
      deleteSepa: false,
      confirmedSepa: false
    };
  },

  computed: {
    paymentMethodItems() {
      return PAYMENT_METHODS.map((paymentMethod) => ({
        value: paymentMethod,
        label: this.$t(`debtors.paymentMethods.${paymentMethod}`)
      }));
    },
    paymentMethodBankTransfer() {
      return PaymentMethod.BANK_TRANSFER;
    },
    debtorHasSepaMandate() {
      return this.debtor.sepaMandate;
    },
    requireSepaData() {
      return (
        this.formData.paymentMethod === PaymentMethod.SEPA_DIRECT_DEBIT || this.debtorHasSepaMandate
      );
    },
    disableSubmit() {
      return this.deleteSepa && !this.debtor.permissions.deleteSepa;
    },
    disableSepaDeleteBtn() {
      return !this.debtor.permissions.deleteSepa && !this.debtor.hasActivePermanentDebts;
    },
    activePermanentDebtsRoute() {
      return {
        name: this.routeName.PERMANENT_DEBTS,
        query: {
          location: this.debtor.locations.map((location) => location.name),
          status: [PermanentDebtStatus.ACTIVE, PermanentDebtStatus.CREATED]
        }
      };
    }
  },

  methods: {
    ...mapActions(NAMESPACE, ['editDebtor']),

    createFormData() {
      return {
        paymentMethod: this.debtor.paymentMethod ?? PAYMENT_METHODS[0],
        sepa: {
          accountOwner: this.debtor.sepaMandate?.accountOwner ?? '',
          iban: this.debtor.sepaMandate?.iban ?? ''
        }
      };
    },

    async onSubmit() {
      if (!(await this.$refs.form.validate())) {
        return;
      }

      const newPaymentData = this.deleteSepa
        ? {
            paymentMethod: PaymentMethod.BANK_TRANSFER,
            sepa: null
          }
        : {
            paymentMethod: this.formData.paymentMethod,
            ...(this.requireSepaData
              ? {
                  sepa: {
                    ...this.formData.sepa
                  }
                }
              : {})
          };

      const payload = filterChangedProperties(newPaymentData, this.oldPaymentData);

      if (Object.keys(payload).length === 0) {
        this.submitted = true;
        return;
      }

      this.loading = true;

      const { error } = await this.editDebtor({ debtorIdentifier: this.debtor.id, payload });

      this.loading = false;
      if (error) {
        this.error = true;
        return;
      }

      this.submitted = true;
    }
  }
};
</script>

<style scoped lang="scss">
.delete-sepa__icon {
  top: -1px;
}
</style>
